import { useState, useEffect } from 'react';
import { Settings, Glasses, Mail, Users, BarChart2, Wrench, Coins } from 'lucide-react';
import { API } from '../partials/profileHelpers';
import { useStatisticsUnlock } from '../../useStatisticsUnlock';

import { useT } from '../../i18n/I18nModule';

function ProfileNavTopBar({ user, currentPage }) {
  const t = useT();
  const [unreadCount, setUnreadCount] = useState(0);
  const { loading: statsGateLoading, unlocked: statsUnlocked } = useStatisticsUnlock(user?.id);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const userIdStr = String(userId).replace(/n$/, '');
    let cancelled = false;

    fetch(`${API}/api/direct-messages/inbox/${encodeURIComponent(userIdStr)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : Array.isArray(data?.messages) ? data.messages : [];
        const unread = list.filter((m) => m && m.messageRead === false).length;
        setUnreadCount(unread);
      })
      .catch(() => {
        if (!cancelled) setUnreadCount(0);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const goTo = (path) => {
    if (typeof window !== 'undefined') {
      window.location.href = path;
    }
  };

  const isProfilePage = currentPage === 'profile';
  const isSettingsPage = currentPage === 'settings';
  const isInboxPage = currentPage === 'inbox';
  const isFriendsPage = currentPage === 'friends';
  const isUsersPage = currentPage === 'users';
  const isStatsPage = currentPage === 'stats';
  const isAdminHubPage = currentPage === 'admin';
  const isKauppaPage = currentPage === 'kauppa';
  const isMember = !!user?.role && user.role !== 'user';
  const asetuksetActive = isSettingsPage;
  const profiiliActive = isProfilePage;
  const isAdmin = !!user && user.role === 'admin';

  return (
    <nav className="ProfileNavTopBar" aria-label={t('nav.accountSections')}>
      <div className="Settings-inbox-bar">
        <button
          type="button"
          className={`Button Settings-inbox-button${asetuksetActive ? ' Settings-inbox-button--active' : ''}`}
          onClick={() => goTo('/asetukset')}
          aria-label={t('nav.settings')}
          title={t('nav.settings')}
        >
          <Settings size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
          <span className="Settings-inbox-button-text">{t('nav.settings')}</span>
        </button>
        <button
          type="button"
          className={`Button Settings-inbox-button${profiiliActive ? ' Settings-inbox-button--active' : ''}`}
          onClick={() => goTo('/profiili')}
          aria-label={t('nav.profile')}
          title={t('nav.profile')}
        >
          <Glasses size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
          <span className="Settings-inbox-button-text">{t('nav.profile')}</span>
        </button>
        <button
          type="button"
          className={`Button Settings-inbox-button${isInboxPage ? ' Settings-inbox-button--active' : ''}`}
          onClick={() => goTo('/postilaatikko')}
          aria-label={t('nav.messages')}
          title={t('nav.messages')}
        >
          <Mail size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
          <span className="Settings-inbox-button-text">{t('nav.messages')}</span>
          {unreadCount > 0 && (
            <span className="Inbox-badge">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className={`Button Settings-inbox-button${
            (isAdmin ? isUsersPage : isFriendsPage) ? ' Settings-inbox-button--active' : ''
          }`}
          onClick={() => goTo(isAdmin ? '/admin/users' : '/kaverit')}
          aria-label={isAdmin ? t('nav.users') : t('nav.friends')}
          title={isAdmin ? t('nav.users') : t('nav.friends')}
        >
          <Users size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
          <span className="Settings-inbox-button-text">{isAdmin ? t('nav.users') : t('nav.friends')}</span>
        </button>
        {statsGateLoading ? (
          <div
            className="Button Settings-inbox-button Settings-inbox-button--stats-placeholder"
            aria-hidden="true"
          >
            <BarChart2 size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
            <span className="Settings-inbox-button-text">{t('nav.stats')}</span>
          </div>
        ) : statsUnlocked ? (
          <button
            type="button"
            className={`Button Settings-inbox-button${isStatsPage ? ' Settings-inbox-button--active' : ''}`}
            onClick={() => goTo('/tilastot')}
            aria-label={t('nav.stats')}
            title={t('nav.stats')}
          >
            <BarChart2 size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
            <span className="Settings-inbox-button-text">{t('nav.stats')}</span>
          </button>
        ) : null}
        {isMember && (
          <button
            type="button"
            className={`Button Settings-inbox-button${isKauppaPage ? ' Settings-inbox-button--active' : ''}`}
            onClick={() => goTo('/kauppa')}
            aria-label={t('nav.shop')}
            title={t('nav.shop')}
          >
            <Coins size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
            <span className="Settings-inbox-button-text">{t('nav.shop')}</span>
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            className={`Button Settings-inbox-button${isAdminHubPage ? ' Settings-inbox-button--active' : ''}`}
            onClick={() => goTo('/admin')}
            aria-label={t('nav.admin')}
            title={t('nav.admin')}
          >
            <Wrench size={20} strokeWidth={2} className="Settings-inbox-button-icon" aria-hidden />
            <span className="Settings-inbox-button-text">{t('nav.admin')}</span>
          </button>
        )}
      </div>
    </nav>
  );
}

export default ProfileNavTopBar;
